import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import type { Artwork, Collection } from "../../types";
import { tokens } from "../../theme";

const CloseIcon = () => <Box component="span">×</Box>;

type Draft = Omit<Collection, "id" | "createdAt">;

const EMPTY: Draft = { name: "", description: "", coverArtId: null };

export default function CollectionFormDialog({
  open,
  initial,
  artworks,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: Collection | null;
  artworks: Artwork[];
  onClose: () => void;
  onSave: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setTouched(false);
      if (initial) {
        const { id: _id, createdAt: _c, ...rest } = initial;
        setDraft(rest);
      } else {
        setDraft(EMPTY);
      }
    }
  }, [open, initial]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const nameValid = draft.name.trim().length > 0;

  // Cover options: pieces already in this collection if editing, otherwise all.
  const coverOptions = initial
    ? artworks.filter((a) => a.collectionId === initial.id)
    : artworks;

  const save = () => {
    setTouched(true);
    if (!nameValid) return;
    onSave({ ...draft, name: draft.name.trim() });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: tokens.bone } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Typography sx={{ fontFamily: tokens.display, fontSize: "1.7rem" }}>
          {initial ? "Edit collection" : "New collection"}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, p: 3 }}>
        <TextField
          label="Name"
          value={draft.name}
          onChange={(e) => set("name", e.target.value)}
          error={touched && !nameValid}
          helperText={
            touched && !nameValid ? "Give the collection a name." : " "
          }
          autoFocus
          fullWidth
        />
        <TextField
          label="Description"
          value={draft.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
        <TextField
          select
          label="Cover image"
          value={draft.coverArtId ?? ""}
          onChange={(e) => set("coverArtId", e.target.value || null)}
          helperText={
            coverOptions.length === 0
              ? "Add pieces to this collection first to choose a cover."
              : "Pick a piece to represent the collection."
          }
          fullWidth
        >
          <MenuItem value="">Auto (first piece)</MenuItem>
          {coverOptions.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.title}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Divider />
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, p: 2.5 }}
      >
        <Button onClick={onClose} sx={{ color: tokens.taupe }}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" color="primary">
          {initial ? "Save changes" : "Create collection"}
        </Button>
      </Box>
    </Dialog>
  );
}
